import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEARTBEAT_INTERVAL = 30000; // 30s
const COUNT_POLL_INTERVAL = 15000; // 15s

function getSessionId() {
  let sid = sessionStorage.getItem("acs_presence_sid");
  if (!sid) {
    sid = "s_" + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem("acs_presence_sid", sid);
  }
  return sid;
}

export default function OnlineCounter() {
  const [count, setCount] = useState(null);
  const location = useLocation();
  const { user } = useAuth();
  const intervalRef = useRef(null);
  const countIntervalRef = useRef(null);

  const sendHeartbeat = useCallback(async () => {
    try {
      const sid = getSessionId();
      await fetch(`${API}/presence/heartbeat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sid,
          authenticated: !!user,
          user_id: user?.id || "",
          user_name: user?.name || user?.email || "",
          current_page: location.pathname,
          user_agent: navigator.userAgent,
          referrer: document.referrer || "",
          screen_width: window.screen.width,
          screen_height: window.screen.height,
          language: navigator.language || "",
        }),
      });
    } catch {}
  }, [user, location.pathname]);

  const fetchCount = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/presence/count`);
      if (resp.ok) {
        const data = await resp.json();
        setCount(data);
      }
    } catch {}
  }, []);

  useEffect(() => {
    sendHeartbeat();
    fetchCount();

    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
    countIntervalRef.current = setInterval(fetchCount, COUNT_POLL_INTERVAL);

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(countIntervalRef.current);
    };
  }, [sendHeartbeat, fetchCount]);

  // Also heartbeat on page change
  useEffect(() => {
    sendHeartbeat();
  }, [location.pathname, sendHeartbeat]);

  if (!count || count.total === 0) return null;

  return (
    <div
      data-testid="online-counter"
      className="fixed bottom-6 left-6 z-[9998] bg-white/90 backdrop-blur-sm border border-[#E5E2DC] rounded-full px-4 py-2 shadow-md flex items-center gap-2 text-xs text-[#595959]"
    >
      <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
      <Users className="w-3.5 h-3.5" />
      <span>
        <span className="font-medium text-[#1C2E4A]">{count.total}</span> online
        {count.logged_in > 0 && (
          <span className="text-[#595959]/60"> ({count.logged_in} members)</span>
        )}
      </span>
    </div>
  );
}
